import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';

export const noteTypes = ['note', 'tip', 'important', 'warning', 'caution'] as const;
export type NoteType = (typeof noteTypes)[number];

/** Both maps mirror iFixit\Wiki\Render\NoteLib, which renders the saved wiki text. */
export const noteTypeLabels: Record<NoteType, string> = {
   note: _js('Note'),
   tip: _js('Tip'),
   important: _js('Important'),
   warning: _js('Warning'),
   caution: _js('Caution'),
};

const noteTypeIcons: Record<NoteType, string> = {
   note: 'circle-info',
   tip: 'lightbulb',
   important: 'message-exclamation',
   warning: 'message-exclamation',
   caution: 'octagon-exclamation',
};

function toNoteType(value: unknown): NoteType {
   const type = typeof value === 'string' ? value.trim() : '';
   return (noteTypes as readonly string[]).includes(type) ? (type as NoteType) : 'note';
}

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      note: {
         toggleNote: (noteType: NoteType) => ReturnType;
         setNoteType: (noteType: NoteType) => ReturnType;
      };
   }
}

export const Note = Node.create<any, any>({
   name: 'note',
   content: 'block*',
   group: 'block',
   renderHTML({ HTMLAttributes }) {
      return ['div', mergeAttributes(HTMLAttributes, { class: 'wiki-note' }), 0];
   },
   addNodeView() {
      return ReactNodeViewRenderer(Component);
   },
   addAttributes() {
      return {
         type: {
            default: null,
            renderHTML: attributes => ({ 'data-note-type': toNoteType(attributes.type) }),
         },
      };
   },
   addCommands() {
      return {
         toggleNote:
            (noteType: NoteType) =>
            ({ chain }) =>
               chain().toggleWrap(this.name).updateAttributes(this.name, { type: noteType }).run(),
         setNoteType:
            (noteType: NoteType) =>
            ({ chain }) =>
               chain().updateAttributes(this.name, { type: noteType }).run(),
      };
   },
});

const Component = (props: { node: { attrs: { type?: NoteType | null } } }) => {
   const type = toNoteType(props.node.attrs.type);
   return (
      <NodeViewWrapper>
         <div className="wiki-note" data-note-type={type}>
            {/* The label is generated, not authored — without this ProseMirror
                treats it as editable content. */}
            <div className="wiki-note-label" contentEditable={false}>
               <FaIcon name={noteTypeIcons[type]} family="duotone" className="size-4" />
               {noteTypeLabels[type]}
            </div>
            <NodeViewContent className="wiki-note-body" />
         </div>
      </NodeViewWrapper>
   );
};
